export const LOGIN_PATH = "./index.html";

const EMAIL_KEY = "demo-user-email";
const AUTH_KEY = "demo-user-authenticated";
const PROFILE_NAME_KEY = "demo-user-display-name";
const PROFILE_PHONE_KEY = "demo-user-phone";
const PROFILE_JOB_TITLE_KEY = "demo-user-job-title";

export type DemoProfile = {
  displayName: string;
  email: string;
  phone: string;
  jobTitle: string;
};

export function getDemoEmail(): string | null {
  return sessionStorage.getItem(EMAIL_KEY);
}

export function setDemoEmail(email: string): void {
  sessionStorage.setItem(EMAIL_KEY, email.trim());
}

export function isDemoAuthenticated(): boolean {
  return sessionStorage.getItem(AUTH_KEY) === "1";
}

export function setDemoAuthenticated(value: boolean): void {
  if (value) {
    sessionStorage.setItem(AUTH_KEY, "1");
  } else {
    sessionStorage.removeItem(AUTH_KEY);
  }
}

export function clearDemoSession(): void {
  sessionStorage.removeItem(EMAIL_KEY);
  sessionStorage.removeItem(AUTH_KEY);
  sessionStorage.removeItem(PROFILE_NAME_KEY);
  sessionStorage.removeItem(PROFILE_PHONE_KEY);
  sessionStorage.removeItem(PROFILE_JOB_TITLE_KEY);
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return email;
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
}

export function initialsFromEmail(email: string): string {
  return initialsFromDisplayName(displayNameFromEmail(email));
}

export function initialsFromDisplayName(name: string): string {
  const bits = name.trim().split(/\s+/).filter(Boolean);
  if (bits.length >= 2) {
    return `${bits[0]![0] ?? ""}${bits[1]![0] ?? ""}`.toUpperCase();
  }
  return (bits[0]?.slice(0, 2) ?? "??").toUpperCase();
}

export function getDemoProfile(email: string): DemoProfile {
  const storedName = sessionStorage.getItem(PROFILE_NAME_KEY)?.trim();
  return {
    displayName: storedName || displayNameFromEmail(email),
    email,
    phone: sessionStorage.getItem(PROFILE_PHONE_KEY) ?? "",
    jobTitle: sessionStorage.getItem(PROFILE_JOB_TITLE_KEY) ?? "",
  };
}

export function saveDemoProfile(profile: DemoProfile): void {
  setDemoEmail(profile.email);
  sessionStorage.setItem(PROFILE_NAME_KEY, profile.displayName.trim());
  sessionStorage.setItem(PROFILE_PHONE_KEY, profile.phone.trim());
  sessionStorage.setItem(PROFILE_JOB_TITLE_KEY, profile.jobTitle.trim());
}

export function requireEmailOrRedirect(loginPath: string): string {
  const email = getDemoEmail();
  if (!email) {
    window.location.href = loginPath;
    throw new Error("redirect");
  }
  return email;
}

export function requireAuthOrRedirect(loginPath: string): string {
  if (!isDemoAuthenticated()) {
    window.location.href = loginPath;
    throw new Error("redirect");
  }
  return requireEmailOrRedirect(loginPath);
}

/** Fill profile row / account detail placeholders from the demo session email. */
export function applyProfileToDocument(email: string): void {
  const profile = getDemoProfile(email);
  const initials = initialsFromDisplayName(profile.displayName);

  document.querySelectorAll("duo-avatar[data-profile-avatar]").forEach((avatar) => {
    avatar.setAttribute("initials", initials);
    avatar.setAttribute("alt", profile.displayName);
  });

  for (const id of ["profileDisplayName", "accountDisplayName"]) {
    const el = document.getElementById(id);
    if (el) el.textContent = profile.displayName;
  }

  for (const id of ["profileEmail", "accountEmail"]) {
    const el = document.getElementById(id);
    if (el) el.textContent = profile.email;
  }
}

export const demoSessions = [
  {
    id: "current",
    title: "MacBook Pro · Chrome",
    subtitle: "Helsinki, Finland · Active now",
    badge: "This device",
    current: true,
  },
  {
    id: "iphone",
    title: "iPhone 15 · Safari",
    subtitle: "Helsinki, Finland · Last active 2 days ago",
  },
  {
    id: "windows",
    title: "Windows PC · Edge",
    subtitle: "Remote · Last active 3 weeks ago",
  },
] as const;

export type DemoSession = (typeof demoSessions)[number];
